// Login.jsx — Page de connexion (redesign éditorial)
// Direction : "Swiss & High-Contrast" + "Editorial Elegance" — cohérent avec Landing.jsx
import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '@/lib/auth';
import { toast } from 'sonner';
import { ArrowRight, Loader2, Sparkles } from 'lucide-react';
import { Logo } from '@/components/Logo';
import { roleHome } from '@/lib/roleHome';

export default function Login() {
  const { login, loading } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [err, setErr] = useState(null);

  const handle = async (e) => {
    e.preventDefault();
    setErr(null);
    try {
      const u = await login(email, password);
      toast.success('Connexion réussie');
      navigate(roleHome(u.role));
    } catch (e) {
      const msg = e?.response?.data?.error || 'Erreur de connexion';
      setErr(msg);
      toast.error(msg);
    }
  };

  return (
    <div className="min-h-screen bg-[#F9F9F8] text-[#0F172A] font-sans antialiased flex" data-testid="login-page">

      {/* ─── Volet visuel gauche (éditorial) ─── */}
      <aside className="hidden lg:flex flex-1 relative bg-[#0A192F] overflow-hidden">
        <div className="absolute inset-0 -z-0 opacity-40" style={{ background: 'radial-gradient(circle at 30% 25%, rgba(0,71,255,0.45), transparent 55%)' }} />
        <div className="absolute -bottom-32 -right-32 w-[420px] h-[420px] rounded-full bg-[#0047FF]/15 blur-3xl" />

        <div className="relative z-10 flex flex-col justify-between p-14 xl:p-16 text-white w-full">
          <Link to="/" className="flex items-center gap-2.5 w-fit group" data-testid="login-logo-link">
            <Logo variant="white" size={30} className="transition-transform group-hover:scale-105" />
            <span className="font-display font-black tracking-tight">AssoPerf</span>
          </Link>

          <div className="max-w-md">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/10 text-[#4DFFD2] text-[10px] font-bold uppercase tracking-[0.2em] mb-7">
              <Sparkles className="w-3 h-3" /> Espace de gestion
            </div>
            <h2 className="font-display text-5xl xl:text-6xl font-black tracking-tight leading-[0.98] mb-6">
              Bon retour <span className="italic font-medium text-[#4DFFD2]">parmi nous.</span>
            </h2>
            <p className="text-base xl:text-lg text-white/70 leading-relaxed">
              Reprenez la gestion de votre association là où vous l'aviez laissée — membres, événements, comptabilité, agents IA.
            </p>
          </div>

          <div className="text-xs text-white/40">
            © {new Date().getFullYear()} AssoPerf · Édité par Osez Studio
          </div>
        </div>
      </aside>

      {/* ─── Volet formulaire droit ─── */}
      <div className="flex-1 grid place-items-center p-6 sm:p-12">
        <div className="w-full max-w-md">

          {/* Logo mobile */}
          <Link to="/" className="lg:hidden flex items-center gap-2.5 mb-10" data-testid="login-mobile-logo">
            <Logo size={30} />
            <span className="font-display font-black tracking-tight text-[#0F172A]">AssoPerf</span>
          </Link>

          <div className="mb-9">
            <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#0047FF] mb-4">Connexion</div>
            <h1 className="font-display text-4xl sm:text-5xl font-black tracking-tight text-[#0F172A] leading-[1.02] mb-3">
              Votre <span className="italic font-medium text-[#0047FF]">espace.</span>
            </h1>
            <p className="text-sm text-[#475569]">
              Pas encore inscrit ?{' '}
              <Link to="/register" className="font-semibold text-[#0F172A] underline underline-offset-4 decoration-[#94A3B8]/50 hover:decoration-[#0047FF] hover:text-[#0047FF] transition-colors" data-testid="login-register-link">
                Créer mon espace
              </Link>
            </p>
          </div>

          <form onSubmit={handle} className="space-y-5" data-testid="login-form">
            <div>
              <label className="block text-[10px] font-bold uppercase tracking-[0.14em] text-[#475569] mb-2">Email</label>
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="ap-input"
                placeholder="vous@exemple.fr"
                data-testid="login-email-input"
              />
            </div>
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="text-[10px] font-bold uppercase tracking-[0.14em] text-[#475569]">Mot de passe</label>
                <Link to="/forgot-password" className="text-[11px] font-semibold text-[#475569] hover:text-[#0047FF] transition-colors" data-testid="login-forgot-link">
                  Oublié ?
                </Link>
              </div>
              <input
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="ap-input"
                placeholder="••••••••"
                data-testid="login-password-input"
              />
            </div>

            {err && (
              <div className="text-sm text-[#B91C1C] bg-[#FEF2F2] border border-[#FECACA] rounded-xl px-4 py-3" data-testid="login-error">
                {err}
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              className="group w-full inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-xl bg-[#0047FF] hover:bg-[#0040E0] text-white font-bold text-base transition-all shadow-md hover:shadow-lg hover:-translate-y-0.5 disabled:opacity-60 disabled:hover:translate-y-0"
              data-testid="login-submit-button"
            >
              {loading ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <>
                  Se connecter
                  <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5" />
                </>
              )}
            </button>
          </form>

        </div>
      </div>
    </div>
  );
}
